import './ForecastResults.scss';
import { ReactElement, useMemo, useState } from 'react';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  GetForecastDataResponse,
  getKpiOutcomeString
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { FullPageSpinner } from '../../../Components/Spinner';
import { useSetError } from '../../../redux/modals';
import { FullForecastSelection, OutcomeKpiForecastData } from '../forecasting-constants';
import { flattenForecastData, getForecastDataRanges } from '../forecasting-utils';
import { ForecastResults } from './ForecastResults';
import { ModelResults } from './ModelResults';

interface ForecastResultsPageProps {
  forecast: FullForecastSelection | undefined;
  forecastData: GetForecastDataResponse | undefined;
  budgetSelectableOptions: number[];
  selectNewBudget: (budget: number) => void;
  cid: string;
}

const ForecastResultsPage = ({
  forecast,
  forecastData,
  budgetSelectableOptions,
  selectNewBudget,
  cid
}: ForecastResultsPageProps): ReactElement => {
  const [chartDateGrouping, setChartDateGrouping] = useState<DateGroupingKey>('Day');
  const setError = useSetError();

  const currentForecastData: OutcomeKpiForecastData | undefined = useMemo(() => {
    if (!forecast || !forecastData) {
      return undefined;
    }

    const kpiOutcomeKey = getKpiOutcomeString({
      outcome: forecast.outcomeVar,
      kpi: forecast.kpiName
    });

    return {
      trainTestData: forecastData.modelTrainTest[kpiOutcomeKey],
      historicalData: forecastData.actualGap[kpiOutcomeKey],
      forecastResultsData: forecastData.forecastResult[kpiOutcomeKey],
      modelSummaryData: forecastData.modelSummary[kpiOutcomeKey]
    };
  }, [forecastData, forecast]);

  const [flattenedData, dataTimestampRanges] = useMemo(() => {
    if (!forecastData) {
      return [];
    }

    return [flattenForecastData(forecastData), getForecastDataRanges(forecastData)];
  }, [forecastData]);

  const flatDataAtKpi = useMemo(() => {
    if (!forecast || !flattenedData) {
      return undefined;
    }

    return flattenedData[
      getKpiOutcomeString({ outcome: forecast.outcomeVar, kpi: forecast.kpiName })
    ];
  }, [flattenedData, forecast]);

  if (
    !forecast ||
    !forecastData ||
    !currentForecastData ||
    !flatDataAtKpi ||
    !dataTimestampRanges
  ) {
    return <FullPageSpinner />;
  }

  return (
    <div className='forecast-results-container' data-testid='forecast-results-container'>
      <ModelResults
        forecast={forecast}
        trainTestData={currentForecastData.trainTestData}
        modelSummaryData={currentForecastData.modelSummaryData}
        chartDateGrouping={chartDateGrouping}
        setChartDateGrouping={setChartDateGrouping}
        setError={setError}
      />
      <ForecastResults
        forecast={forecast}
        flatForecastData={flatDataAtKpi}
        dataTimestampRanges={dataTimestampRanges}
        forecastResultsData={currentForecastData.forecastResultsData}
        budgetSelectableOptions={budgetSelectableOptions}
        selectNewBudget={selectNewBudget}
        chartDateGrouping={chartDateGrouping}
        setChartDateGrouping={setChartDateGrouping}
        cid={cid}
        setError={setError}
      />
    </div>
  );
};

export { ForecastResultsPage };
